import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div`
  grid-area: details;
`

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  optionName?: string
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  selectedLoyaltyTargetUpgradePackage?: App.Package
}

function AccommodationSameRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    optionName,
    stayPayRateTier,
    selectedLoyaltyTargetUpgradePackage,
  } = props

  const intl = useIntl()

  const targetUpgradeName = selectedLoyaltyTargetUpgradePackage?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'e3D2ed1NEg',
          defaultMessage: 'Option {number}',
          description: 'Label showing the package option number in the same-room option banner.',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: 'yJdvrOHnwC',
          defaultMessage: '{tierName} member upgrade',
          description: 'Label indicating that this option includes a loyalty member upgrade (same-room banner).',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2+S47pPpwv',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name (same-room banner).',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const nightFreeSuffix = stayPayRateTier
    ? intl.formatMessage(
        {
          id: 'Cisg4JPfu+',
          defaultMessage: ', {count, plural, one {# night free} other {# nights free}}',
          description: 'Suffix appended to a package name indicating included free nights (same-room banner).',
        },
        { count: stayPayRateTier.freeNights },
      )
    : undefined

  return <Container>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {/* TODO: i18n - pipe separator (' | ') between labels is a visual/structural pattern that may need locale-specific handling */}
        {!!packageNumber && <Caption variant="large">
          {packageOptionLabel}
          {!!memberUpgradeLabel && <> | <b>{memberUpgradeLabel}</b></>}
        </Caption>}
        {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
        {!!targetUpgradeName && <BodyText variant="large" weight="bold">
          {upgradedToLabel}
        </BodyText>}
        {!!optionName && <BodyText
          variant="large"
          weight="bold"
          colour={selectedLoyaltyTargetUpgradePackage ? 'neutral-four' : 'neutral-one'}
          strikeThrough={!!selectedLoyaltyTargetUpgradePackage}
        >
          {optionName}
          {nightFreeSuffix}
        </BodyText>}
      </VerticalSpacer>
      {children}
    </VerticalSpacer>
  </Container>
}

export default AccommodationSameRoomOptionTileDetails
